import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Search, Upload, FileText, Eye, CheckCircle, Download, RotateCw, Check } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Pagination } from '@/components/ui/pagination';
import { useDocuments, useDocument, useUpdateDocumentStatus, useConfirmExtraction, useReprocessDocument, useClients, useUploadDocument } from '@/hooks/queries';
import { DocumentStatus, DocumentType, ExpenseCategory } from '@taxistry/types';
import type { Document as TaxistryDocument, AiExtraction } from '@taxistry/types';

const DOCUMENT_TYPE_LABELS: Record<string, string> = {
  [DocumentType.TIMOLOGIO_POLISIS]: 'Τιμολόγιο Πώλησης',
  [DocumentType.TIMOLOGIO_PAROHIS_YPIRESION]: 'Τιμολόγιο Παροχής Υπηρεσιών',
  [DocumentType.APODEIXI]: 'Απόδειξη',
  [DocumentType.PISTOTIKO]: 'Πιστωτικό',
  [DocumentType.LOGARIASMOS_DEKO]: 'Λογαριασμός ΔΕΚΟ',
  [DocumentType.APODEIXI_ENOIKIOU]: 'Απόδειξη Ενοικίου',
  [DocumentType.OTHER]: 'Άλλο',
};

const EXPENSE_CATEGORY_LABELS: Record<string, string> = {
  [ExpenseCategory.TILEKOINONIES]: 'Τηλεπικοινωνίες',
  [ExpenseCategory.ENOIKIA]: 'Ενοίκια',
  [ExpenseCategory.YLIKA]: 'Υλικά',
  [ExpenseCategory.METAFORIKA]: 'Μεταφορικά',
  [ExpenseCategory.KAFETERIA_ESTIASI]: 'Καφετέρια / Εστίαση',
  [ExpenseCategory.LOGISTIKES_YPIRESIES]: 'Λογιστικές Υπηρεσίες',
  [ExpenseCategory.ENERGIA]: 'Ενέργεια',
  [ExpenseCategory.YDREFSI]: 'Ύδρευση',
  [ExpenseCategory.MISTHODOSIA]: 'Μισθοδοσία',
  [ExpenseCategory.ASFALISTIKES_EISFORES]: 'Ασφαλιστικές Εισφορές',
  [ExpenseCategory.OTHER]: 'Άλλο',
};

const STATUS_OPTIONS: { value: DocumentStatus | ''; label: string }[] = [
  { value: '', label: 'Όλα' },
  { value: DocumentStatus.NEW, label: 'Νέο' },
  { value: DocumentStatus.REVIEWING, label: 'Σε Εξέταση' },
  { value: DocumentStatus.REVIEWED, label: 'Εξετασμένο' },
  { value: DocumentStatus.POSTED, label: 'Καταχωρημένο' },
];

export function DocumentsPage() {
  const { t } = useTranslation();
  const [statusFilter, setStatusFilter] = useState<DocumentStatus | ''>('');
  const [clientFilter, setClientFilter] = useState<string>('');
  const [searchQuery, setSearchQuery] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [selectedDocId, setSelectedDocId] = useState<string | null>(null);
  const [showUpload, setShowUpload] = useState(false);

  const { data, isLoading } = useDocuments({
    page,
    limit,
    status: (statusFilter || undefined) as DocumentStatus | undefined,
    clientId: clientFilter || undefined,
    search: searchQuery || undefined,
    dateFrom: dateFrom || undefined,
    dateTo: dateTo || undefined,
    sortBy: 'createdAt',
    sortOrder: 'desc',
  } as Record<string, unknown>);

  const { data: clientsList } = useClients({ limit: 1000 });

  return (
    <div className="p-6 space-y-4">
      <div className="flex items-center justify-between">
        <h1 className="font-display text-xl font-bold">{t('documents.title')}</h1>
        <div className="flex gap-2">
          <Button size="sm" className="gradient-primary text-white rounded-[12px]" onClick={() => setShowUpload(true)}>
            <Upload className="h-4 w-4 mr-2" />
            Ανέβασμα
          </Button>
          <Button variant="outline" size="sm" asChild>
            <a href="/api/documents/export/csv" target="_blank" rel="noopener">
              <Download className="h-4 w-4 mr-2" />
              CSV
            </a>
          </Button>
        </div>
      </div>

      {/* Filters */}
      <Card className="shadow-none border-border">
        <CardContent className="p-4">
          <div className="flex flex-wrap gap-3">
            <div className="relative">
              <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Αναζήτηση (προμηθευτής, ΑΦΜ)"
                value={searchQuery}
                onChange={(e) => { setSearchQuery(e.target.value); setPage(1); }}
                className="pl-9 w-[240px] rounded-[10px] bg-surface-2"
              />
            </div>

            <Select
              value={statusFilter}
              onValueChange={(v) => { setStatusFilter((v === '__all__' ? '' : v) as DocumentStatus | ''); setPage(1); }}
            >
              <SelectTrigger className="w-[180px]">
                <SelectValue placeholder="Κατάσταση" />
              </SelectTrigger>
              <SelectContent>
                {STATUS_OPTIONS.map((opt) => (
                  <SelectItem key={opt.value || '__all__'} value={opt.value || '__all__'}>
                    {opt.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Select
              value={clientFilter}
              onValueChange={(v) => { setClientFilter(v === '__all__' ? '' : v); setPage(1); }}
            >
              <SelectTrigger className="w-[240px]">
                <SelectValue placeholder="Πελάτης" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="__all__">Όλοι οι πελάτες</SelectItem>
                {clientsList?.data.map((c) => (
                  <SelectItem key={c.id} value={c.id}>{c.businessName}</SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Input
              type="date"
              value={dateFrom}
              onChange={(e) => { setDateFrom(e.target.value); setPage(1); }}
              className="w-[160px]"
              placeholder="Από"
            />
            <Input
              type="date"
              value={dateTo}
              onChange={(e) => { setDateTo(e.target.value); setPage(1); }}
              className="w-[160px]"
              placeholder="Έως"
            />
          </div>
        </CardContent>
      </Card>

      {/* Document list */}
      <div className="space-y-2">
        {isLoading ? (
          Array.from({ length: 5 }).map((_, i) => (
            <Card key={i}>
              <CardContent className="p-4">
                <Skeleton className="h-16 w-full" />
              </CardContent>
            </Card>
          ))
        ) : !data?.data.length ? (
          <Card>
            <CardContent className="p-12 text-center">
              <FileText className="h-12 w-12 mx-auto text-muted-foreground/50 mb-3" />
              <p className="text-muted-foreground">{t('documents.noDocuments')}</p>
            </CardContent>
          </Card>
        ) : (
          data.data.map((doc) => (
            <DocumentRow
              key={doc.id}
              doc={doc}
              onSelect={() => setSelectedDocId(doc.id)}
            />
          ))
        )}
      </div>

      {/* Pagination */}
      {data && (
        <Pagination
          page={data.page}
          totalPages={data.totalPages}
          total={data.total}
          limit={limit}
          onPageChange={setPage}
          onLimitChange={setLimit}
        />
      )}

      {/* Document preview dialog */}
      {selectedDocId && (
        <DocumentPreviewDialog
          docId={selectedDocId}
          onClose={() => setSelectedDocId(null)}
        />
      )}

      {/* Upload dialog */}
      {showUpload && (
        <UploadDialog
          clients={clientsList?.data ?? []}
          onClose={() => setShowUpload(false)}
        />
      )}
    </div>
  );
}

// ─── Document Row ─────────────────────────────

function DocumentRow({
  doc,
  onSelect,
}: {
  doc: TaxistryDocument & { clientBusinessName?: string };
  onSelect: () => void;
}) {
  const { t } = useTranslation();
  const updateStatus = useUpdateDocumentStatus();

  const statusVariant = doc.status === DocumentStatus.NEW ? 'default'
    : doc.status === DocumentStatus.REVIEWING ? 'warning'
    : doc.status === DocumentStatus.REVIEWED ? 'success'
    : 'success';

  const fileSizeMb = (doc.fileSizeBytes / 1024 / 1024).toFixed(1);
  const extraction = doc.aiExtraction;

  const isPdf = doc.mimeType === 'application/pdf';
  const isImage = doc.mimeType.startsWith('image/');
  const ext = doc.originalFilename.split('.').pop()?.toUpperCase() ?? '';
  const iconBg = isPdf ? 'bg-destructive/10 text-destructive' : isImage ? 'bg-primary/10 text-primary' : 'bg-muted text-muted-foreground';

  return (
    <Card className="hover:shadow-lifted hover:-translate-y-px group rounded-2xl border-border">
      <CardContent className="p-4">
        <div className="flex items-start gap-4">
          {/* Icon */}
          <div className={`${iconBg} p-2.5 rounded-xl shrink-0 flex flex-col items-center justify-center min-w-[44px]`}>
            <FileText className="h-5 w-5" />
            <span className="text-xs font-data font-semibold mt-0.5">{ext}</span>
          </div>

          {/* Content */}
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-1">
              <button onClick={onSelect} className="font-medium text-sm hover:underline truncate text-left">
                {doc.originalFilename}
              </button>
              <Badge variant={statusVariant} className="shrink-0 hover:shadow-sm">
                {t(`documents.status.${doc.status}`)}
              </Badge>
            </div>
            <div className="flex items-center gap-3 text-xs text-muted-foreground">
              <span>{doc.clientBusinessName ?? '—'}</span>
              <span>&middot;</span>
              <span>{new Date(doc.createdAt).toLocaleDateString('el-GR')}</span>
              <span>&middot;</span>
              <span className="font-data">{fileSizeMb} MB</span>
              <span>&middot;</span>
              <span>{doc.uploadSource === 'PORTAL' ? 'Portal' : doc.uploadSource === 'EMAIL' ? 'Email' : 'Dashboard'}</span>
              {doc.aiDocumentType ? (
                <>
                  <span>&middot;</span>
                  <span>{DOCUMENT_TYPE_LABELS[doc.aiDocumentType] ?? doc.aiDocumentType}</span>
                </>
              ) : null}
              {doc.aiExpenseCategory ? (
                <>
                  <span>&middot;</span>
                  <span>{EXPENSE_CATEGORY_LABELS[doc.aiExpenseCategory] ?? doc.aiExpenseCategory}</span>
                </>
              ) : null}
            </div>
            {extraction && (
              <div className="flex items-center gap-4 mt-2 text-xs">
                {extraction.issuerName && (
                  <span>{t('documents.supplier')}: <strong>{extraction.issuerName}</strong></span>
                )}
                {extraction.totalAmount != null && (
                  <span>{t('documents.amount')}: <strong>{extraction.totalAmount.toFixed(2)}€</strong></span>
                )}
                {extraction.date && (
                  <span>{t('documents.date')}: <strong>{extraction.date}</strong></span>
                )}
              </div>
            )}
          </div>

          {/* Actions */}
          <div className="flex gap-1 shrink-0">
            <Button variant="ghost" size="icon" onClick={onSelect} title="Προβολή">
              <Eye className="h-4 w-4" />
            </Button>
            {doc.status === DocumentStatus.NEW && (
              <Button
                variant="ghost"
                size="icon"
                title="Σε εξέταση"
                onClick={() => updateStatus.mutate({ id: doc.id, status: DocumentStatus.REVIEWING })}
              >
                <CheckCircle className="h-4 w-4" />
              </Button>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

// ─── Document Preview Dialog ──────────────────

function DocumentPreviewDialog({ docId, onClose }: { docId: string; onClose: () => void }) {
  const { t } = useTranslation();
  const { data: doc, isLoading } = useDocument(docId);
  const updateStatus = useUpdateDocumentStatus();
  const confirmExtraction = useConfirmExtraction();
  const reprocess = useReprocessDocument();
  const [editing, setEditing] = useState(false);
  const [editData, setEditData] = useState<Record<string, string | number | null>>({});

  const startEditing = () => {
    if (!doc?.aiExtraction) return;
    setEditData({
      issuerAfm: doc.aiExtraction.issuerAfm ?? '',
      issuerName: doc.aiExtraction.issuerName ?? '',
      invoiceNumber: doc.aiExtraction.invoiceNumber ?? '',
      date: doc.aiExtraction.date ?? '',
      netAmount: doc.aiExtraction.netAmount,
      vatAmount: doc.aiExtraction.vatAmount,
      totalAmount: doc.aiExtraction.totalAmount,
      vatRate: doc.aiExtraction.vatRate,
    });
    setEditing(true);
  };

  const handleConfirm = () => {
    if (!doc) return;
    const extraction: Record<string, unknown> = {
      ...(doc.aiExtraction ?? {}),
      issuerAfm: editData.issuerAfm || null,
      issuerName: editData.issuerName || null,
      invoiceNumber: editData.invoiceNumber || null,
      date: editData.date || null,
      netAmount: editData.netAmount != null && editData.netAmount !== '' ? Number(editData.netAmount) : null,
      vatAmount: editData.vatAmount != null && editData.vatAmount !== '' ? Number(editData.vatAmount) : null,
      totalAmount: editData.totalAmount != null && editData.totalAmount !== '' ? Number(editData.totalAmount) : null,
      vatRate: editData.vatRate != null && editData.vatRate !== '' ? Number(editData.vatRate) : null,
    };
    confirmExtraction.mutate(
      { id: doc.id, extraction },
      {
        onSuccess: () => {
          setEditing(false);
        },
      },
    );
  };

  const updateField = (field: string, value: string) => {
    setEditData((prev) => ({ ...prev, [field]: value }));
  };

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle>{doc?.originalFilename ?? t('common.loading')}</DialogTitle>
        </DialogHeader>

        {isLoading ? (
          <div className="flex-1 flex items-center justify-center">
            <Skeleton className="h-96 w-full" />
          </div>
        ) : doc ? (
          <div className="flex-1 grid grid-cols-2 gap-4 overflow-hidden">
            {/* Document preview */}
            <div className="border border-border rounded-lg overflow-auto bg-muted/30">
              {doc.mimeType.startsWith('image/') ? (
                <img src={doc.signedUrl} alt={doc.originalFilename} className="w-full object-contain" />
              ) : doc.mimeType === 'application/pdf' ? (
                <iframe src={doc.signedUrl} className="w-full h-full min-h-[400px]" title="PDF preview" />
              ) : (
                <div className="flex items-center justify-center h-64 text-muted-foreground">
                  Δεν υποστηρίζεται η προεπισκόπηση αυτού του τύπου αρχείου
                </div>
              )}
            </div>

            {/* Extraction data */}
            <div className="overflow-auto space-y-4">
              <div>
                <div className="flex items-center justify-between mb-2">
                  <h3 className="font-medium text-sm">{t('documents.aiExtraction')}</h3>
                  {doc.aiExtraction && !editing && !doc.confirmedExtraction && (
                    <Button variant="outline" size="sm" onClick={startEditing}>
                      Επεξεργασία
                    </Button>
                  )}
                  {doc.confirmedExtraction && (
                    <Badge variant="success" className="hover:shadow-sm">Επιβεβαιωμένο</Badge>
                  )}
                </div>
                {doc.aiExtraction ? (
                  editing ? (
                    <div className="space-y-3 text-sm">
                      <EditableField label={t('documents.supplier')} value={String(editData.issuerName ?? '')} onChange={(v) => updateField('issuerName', v)} />
                      <EditableField label="ΑΦΜ Εκδότη" value={String(editData.issuerAfm ?? '')} onChange={(v) => updateField('issuerAfm', v)} />
                      <EditableField label={t('documents.invoiceNumber')} value={String(editData.invoiceNumber ?? '')} onChange={(v) => updateField('invoiceNumber', v)} />
                      <EditableField label={t('documents.date')} value={String(editData.date ?? '')} onChange={(v) => updateField('date', v)} type="date" />
                      <EditableField label="Καθαρή Αξία (€)" value={editData.netAmount != null ? String(editData.netAmount) : ''} onChange={(v) => updateField('netAmount', v)} type="number" />
                      <EditableField label="ΦΠΑ (€)" value={editData.vatAmount != null ? String(editData.vatAmount) : ''} onChange={(v) => updateField('vatAmount', v)} type="number" />
                      <EditableField label={`${t('documents.amount')} (€)`} value={editData.totalAmount != null ? String(editData.totalAmount) : ''} onChange={(v) => updateField('totalAmount', v)} type="number" />
                      <EditableField label="ΦΠΑ %" value={editData.vatRate != null ? String(editData.vatRate) : ''} onChange={(v) => updateField('vatRate', v)} type="number" />
                      <div className="flex gap-2 pt-2">
                        <Button size="sm" onClick={handleConfirm} disabled={confirmExtraction.isPending}>
                          {confirmExtraction.isPending ? 'Αποθήκευση...' : 'Επιβεβαίωση'}
                        </Button>
                        <Button size="sm" variant="outline" onClick={() => setEditing(false)}>
                          Ακύρωση
                        </Button>
                      </div>
                    </div>
                  ) : (
                    <div className="space-y-2 text-sm">
                      {(() => {
                        const data = doc.confirmedExtraction ?? doc.aiExtraction;
                        return (
                          <>
                            <ExtractionField label={t('documents.supplier')} value={data.issuerName} />
                            <ExtractionField label="ΑΦΜ Εκδότη" value={data.issuerAfm} />
                            <ExtractionField label={t('documents.invoiceNumber')} value={data.invoiceNumber} />
                            <ExtractionField label={t('documents.date')} value={data.date} />
                            <ExtractionField label="Καθαρή Αξία" value={data.netAmount != null ? `${data.netAmount.toFixed(2)}€` : null} />
                            <ExtractionField label="ΦΠΑ" value={data.vatAmount != null ? `${data.vatAmount.toFixed(2)}€` : null} />
                            <ExtractionField label={t('documents.amount')} value={data.totalAmount != null ? `${data.totalAmount.toFixed(2)}€` : null} />
                            <ExtractionField label="ΦΠΑ %" value={data.vatRate} />
                            <ExtractionField label="Τύπος Εγγράφου" value={doc.aiDocumentType ? (DOCUMENT_TYPE_LABELS[doc.aiDocumentType] ?? doc.aiDocumentType) : null} />
                            <ExtractionField label="Κατηγορία Δαπάνης" value={doc.aiExpenseCategory ? (EXPENSE_CATEGORY_LABELS[doc.aiExpenseCategory] ?? doc.aiExpenseCategory) : null} />
                          </>
                        );
                      })()}
                      {doc.aiConfidence != null && (
                        <div className="pt-2">
                          <Badge variant={doc.aiConfidence > 0.8 ? 'success' : doc.aiConfidence > 0.5 ? 'warning' : 'error'} className="hover:shadow-sm">
                            Βεβαιότητα: {(doc.aiConfidence * 100).toFixed(0)}%
                          </Badge>
                        </div>
                      )}
                    </div>
                  )
                ) : (
                  <p className="text-sm text-muted-foreground">Δεν έχει γίνει AI εξαγωγή ακόμα</p>
                )}
              </div>

              {/* Status + actions */}
              <div className="pt-4 border-t space-y-3">
                <h3 className="font-medium text-sm">Κατάσταση</h3>
                <StatusPills
                  currentStatus={doc.status as DocumentStatus}
                  onChangeStatus={(status) => updateStatus.mutate({ id: doc.id, status })}
                  isPending={updateStatus.isPending}
                  isSuccess={updateStatus.isSuccess}
                />
                {!doc.confirmedExtraction && doc.status !== DocumentStatus.POSTED && (
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => reprocess.mutate(doc.id)}
                    disabled={reprocess.isPending}
                  >
                    <RotateCw className={`h-4 w-4 mr-1.5 ${reprocess.isPending ? 'animate-spin' : ''}`} />
                    {reprocess.isPending ? 'Επεξεργασία...' : 'Επανεπεξεργασία AI'}
                  </Button>
                )}
              </div>
            </div>
          </div>
        ) : null}
      </DialogContent>
    </Dialog>
  );
}

// ─── Upload Dialog ───────────────────────────

function UploadDialog({
  clients,
  onClose,
}: {
  clients: Array<{ id: string; businessName: string }>;
  onClose: () => void;
}) {
  const [selectedClient, setSelectedClient] = useState('');
  const [file, setFile] = useState<File | null>(null);
  const upload = useUploadDocument();

  const handleUpload = () => {
    if (!file || !selectedClient) return;
    upload.mutate(
      { file, clientId: selectedClient },
      { onSuccess: () => onClose() },
    );
  };

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Ανέβασμα εγγράφου</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div>
            <label className="text-sm font-medium mb-1.5 block">Πελάτης</label>
            <Select value={selectedClient} onValueChange={setSelectedClient}>
              <SelectTrigger>
                <SelectValue placeholder="Επιλέξτε πελάτη" />
              </SelectTrigger>
              <SelectContent>
                {clients.map((c) => (
                  <SelectItem key={c.id} value={c.id}>{c.businessName}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="text-sm font-medium mb-1.5 block">Αρχείο</label>
            <Input
              type="file"
              accept="image/*,application/pdf"
              onChange={(e) => setFile(e.target.files?.[0] ?? null)}
            />
          </div>
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={onClose}>Ακύρωση</Button>
            <Button onClick={handleUpload} disabled={!file || !selectedClient || upload.isPending}>
              {upload.isPending ? 'Ανέβασμα...' : 'Ανέβασμα'}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ─── Status Pills ───────────────────────────

const STATUS_PILL_STYLES: Record<DocumentStatus, { active: string; idle: string }> = {
  [DocumentStatus.NEW]: {
    active: 'bg-gray-600 text-white border-gray-600',
    idle: 'border-gray-300 text-gray-500 hover:border-gray-400 hover:text-gray-700',
  },
  [DocumentStatus.REVIEWING]: {
    active: 'bg-yellow-500 text-white border-yellow-500',
    idle: 'border-yellow-300 text-yellow-600 hover:border-yellow-400 hover:text-yellow-700',
  },
  [DocumentStatus.REVIEWED]: {
    active: 'bg-blue-600 text-white border-blue-600',
    idle: 'border-blue-300 text-blue-500 hover:border-blue-400 hover:text-blue-700',
  },
  [DocumentStatus.POSTED]: {
    active: 'bg-green-600 text-white border-green-600',
    idle: 'border-green-300 text-green-600 hover:border-green-400 hover:text-green-700',
  },
};

const STATUS_LABELS: Record<DocumentStatus, string> = {
  [DocumentStatus.NEW]: 'Νέο',
  [DocumentStatus.REVIEWING]: 'Σε Εξέταση',
  [DocumentStatus.REVIEWED]: 'Εξετασμένο',
  [DocumentStatus.POSTED]: 'Καταχωρημένο',
};

function StatusPills({
  currentStatus,
  onChangeStatus,
  isPending,
  isSuccess,
}: {
  currentStatus: DocumentStatus;
  onChangeStatus: (status: DocumentStatus) => void;
  isPending: boolean;
  isSuccess: boolean;
}) {
  const [flash, setFlash] = useState(false);

  useEffect(() => {
    if (isSuccess) {
      setFlash(true);
      const t = setTimeout(() => setFlash(false), 1200);
      return () => clearTimeout(t);
    }
  }, [isSuccess]);

  const allStatuses = [DocumentStatus.NEW, DocumentStatus.REVIEWING, DocumentStatus.REVIEWED, DocumentStatus.POSTED];

  return (
    <div className="flex items-center gap-1">
      {allStatuses.map((status) => {
        const isActive = status === currentStatus;
        const styles = STATUS_PILL_STYLES[status];
        return (
          <button
            key={status}
            disabled={isPending || isActive}
            onClick={() => onChangeStatus(status)}
            className={`
              px-3 py-1.5 text-xs font-medium rounded-full border transition-all duration-200
              inline-flex items-center gap-1
              disabled:cursor-default hover:shadow-sm
              ${isActive ? styles.active : styles.idle}
              ${isActive && flash ? 'ring-2 ring-offset-1 ring-green-400 animate-pulse' : ''}
            `}
          >
            {isActive && <Check className="h-3 w-3" />}
            {STATUS_LABELS[status]}
          </button>
        );
      })}
    </div>
  );
}

function ExtractionField({ label, value }: { label: string; value: string | number | null | undefined }) {
  if (value == null) return null;
  return (
    <div className="flex justify-between">
      <span className="text-muted-foreground">{label}</span>
      <span className="font-medium">{value}</span>
    </div>
  );
}

function EditableField({
  label,
  value,
  onChange,
  type = 'text',
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
  type?: 'text' | 'number' | 'date';
}) {
  return (
    <div className="grid grid-cols-[1fr_1.5fr] gap-2 items-center">
      <label className="text-muted-foreground text-xs">{label}</label>
      <Input
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="h-8 text-sm"
        step={type === 'number' ? '0.01' : undefined}
      />
    </div>
  );
}
